import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { DashboardLayout } from './layouts/DashboardLayout';
import { Home } from './pages/public/Home';
import { Login } from './pages/public/Login';
import { Signup } from './pages/public/Signup';
import { ClientDashboard } from './pages/client/Dashboard';
import { DocumentWizard } from './pages/client/DocumentWizard';
import { ClientSettings } from './pages/client/Settings';
import { AdvisorDashboard } from './pages/advisor/Dashboard';
import { DocumentReview } from './pages/advisor/DocumentReview';
import { AdminDashboard } from './pages/admin/Dashboard';

function AuthLoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
      <div
        className="w-10 h-10 border-2 border-brand-navy border-t-transparent rounded-full animate-spin"
        aria-hidden
      />
      <p className="text-sm text-slate-600">Loading...</p>
    </div>
  );
}

function dashboardPathForRole(r) {
  if (r === 'client') return '/client/dashboard';
  if (r === 'advisor') return '/advisor/dashboard';
  if (r === 'admin') return '/admin/dashboard';
  return '/login';
}

/** Login / signup: wait for auth init, then send signed-in users to their dashboard. */
function PublicAuthRoute({ children }) {
  const { user, role, loading } = useAuth();

  if (loading) return <AuthLoadingScreen />;
  if (user && role) return <Navigate to={dashboardPathForRole(role)} replace />;
  return children;
}

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, role, loading } = useAuth();

  if (loading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!role) return <Navigate to="/login" replace />;

  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={dashboardPathForRole(role)} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<PublicAuthRoute><Login /></PublicAuthRoute>} />
      <Route path="/signup" element={<PublicAuthRoute><Signup /></PublicAuthRoute>} />

      <Route element={<DashboardLayout />}>
        {/* Client */}
        <Route path="/client/dashboard" element={
          <ProtectedRoute allowedRoles={['client']}><ClientDashboard /></ProtectedRoute>
        } />
        <Route path="/client/document/:id" element={
          <ProtectedRoute allowedRoles={['client', 'advisor']}><DocumentWizard /></ProtectedRoute>
        } />
        <Route path="/client/settings" element={
          <ProtectedRoute allowedRoles={['client']}><ClientSettings /></ProtectedRoute>
        } />

        {/* Advisor */}
        <Route path="/advisor/dashboard" element={
          <ProtectedRoute allowedRoles={['advisor']}><AdvisorDashboard /></ProtectedRoute>
        } />
        <Route path="/advisor/document/:id" element={
          <ProtectedRoute allowedRoles={['advisor']}><DocumentReview /></ProtectedRoute>
        } />
        <Route path="/advisor/settings" element={
          <ProtectedRoute allowedRoles={['advisor']}>
            <div className="p-8">Advisor Settings (Coming Soon)</div>
          </ProtectedRoute>
        } />

        {/* Admin */}
        <Route path="/admin/dashboard" element={
          <ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>
        } />
        <Route path="/admin/vouchers" element={
          <ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>
        } />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}
